import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBlock } from '@/hooks/rxdb-models';
import { useBlockInstructions } from '@/hooks/use-block-instructions';

import { toTiptapHtml } from '@flintmint/core-shared/utils';

import { getEditorHTML, isEqualWithHTML } from '@/utils/editor-utils';

import type { EditorOptions } from '@tiptap/react';
import type { Block } from '@flintmint/core-shared/rxdb';
import type { BlockContext } from '@/constants/types';
import { createBlankEditorContent } from '@/hooks/rxdb-models/use-notes';

const namePlaceholder = 'Untitled Block';
const emptyTagIds: string[] = [];

type SavedBlockState = {
  name: string;
  tagIds: string[];
  content: string;
  instructions: Record<string, boolean>;
};

export const useBlockEditorState = (props: UseBlockEditorStateProps) => {
  const { open, blockId, draftId, context: { initialName, initialContent } = {} } = props;

  const [name, setName] = useState(namePlaceholder);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  const [savedState, setSavedState] = useState<SavedBlockState>(() => buildSavedBlockState());
  const [content, setContent] = useState(savedState.content);

  const { data: block } = useBlock(blockId ?? draftId);
  const blockJSON = useMemo(() => block?.toJSON(), [block]) as Block;

  const {
    blockInstructions,
    onInstructionCreate,
    onInstructionDelete,
    resetBlockInstructions,
    setBlocksInstructionsData,
    onInstructionEnabledChange,
  } = useBlockInstructions();

  // Read by applySavedState, which runs from an effect and must see the values of the latest commit.
  const localStateRef = useRef({ name, tagIds, blockInstructions, savedState });
  useEffect(() => {
    localStateRef.current = { name, tagIds, blockInstructions, savedState };
  });

  const resetState = () => {
    const nextSavedState = buildSavedBlockState();

    setTagIds([]);
    setName(namePlaceholder);
    setContent(nextSavedState.content);
    setSavedState(nextSavedState);
    resetBlockInstructions();
    setReady(false);
  };

  const commitSavedState = async (nextBlock: Partial<Block>) => {
    const nextSavedState = buildSavedBlockState(nextBlock);
    setSavedState(nextSavedState);
  };

  const onEditorUpdate: EditorOptions['onUpdate'] = useCallback(({ editor }) => {
    setContent(getEditorHTML(editor));
  }, []);

  const applySavedState = useCallback(
    (nextSavedState: SavedBlockState) => {
      const { name, tagIds, blockInstructions, savedState } = localStateRef.current;

      if (isEqualWithHTML({ tagIds }, { tagIds: savedState.tagIds })) setTagIds(nextSavedState.tagIds);
      if (isEqualWithHTML({ name }, { name: savedState.name })) setName(nextSavedState.name);
      setContent(nextSavedState.content);
      setSavedState(nextSavedState);
      setReady(true);

      if (!isEqualWithHTML({ instructions: blockInstructions }, { instructions: savedState.instructions })) return;

      if (Object.keys(nextSavedState.instructions).length) {
        setBlocksInstructionsData(nextSavedState.instructions);
        return;
      }

      resetBlockInstructions();
    },
    [resetBlockInstructions, setBlocksInstructionsData]
  );

  useEffect(() => {
    if (!open) return;

    if (blockJSON) {
      applySavedState(buildSavedBlockState(blockJSON));
      return;
    }

    if (initialContent || initialName) {
      applySavedState(
        buildSavedBlockState({
          name: initialName,
          content: initialContent,
        })
      );
      return;
    }

    if (!blockId) {
      applySavedState(buildSavedBlockState());
    }
  }, [applySavedState, blockId, blockJSON, initialContent, initialName, open]);

  return {
    name,
    block,
    tagIds,
    setName,
    content,
    ready,
    setTagIds,
    setContent,
    resetState,
    commitSavedState,
    savedState,
    onEditorUpdate,
    blockInstructions,
    onInstructionCreate,
    onInstructionDelete,
    onInstructionEnabledChange,
  };
};

interface UseBlockEditorStateProps {
  open: boolean;
  blockId?: string;
  draftId?: string;
  context?: BlockContext;
}

function buildSavedBlockState(block?: Partial<Block>): SavedBlockState {
  const content = block?.content ?? createBlankEditorContent();

  return {
    tagIds: block?.tagIds ?? emptyTagIds,
    name: block?.name ?? namePlaceholder,
    content: toTiptapHtml(content),
    instructions: block?.instructions ?? {},
  };
}
